import { DynamicForm } from "@shared/components/DynamicForm";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { Modal } from "@shared/ui/modal";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  AlertCircle,
  Banknote,
  CheckCircle2,
  CreditCard,
  DollarSign,
  Droplet,
  FileText,
  Key,
  Layers,
  LetterText,
  LucideUserSquare,
  Mail,
  MapPin,
  Phone,
  ReceiptText,
  Search,
  Send,
  ShieldCheck,
  User,
  UserPlus,
  Users,
  XCircle,
  Zap,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { reconnectionApi } from "@features/cx/reconnect/api";
import { SearchInput } from "@shared/components/SearchInput";
import { Badge } from "@shared/ui/badge";
import {
  useApproveBill,
  useApproveDisconnectionBill,
  useBillTemplates,
  useConsumerBill,
  useDisconnectionRequestDetail,
  useMeterReadings,
  usePrintBill,
  useRefundRemainingAmountBill,
  useRejectBill,
  useSubmitConsumerSelection,
  useUpdateDisconnection,
  useUpdateDisconnectionRequest,
  useMasterAccountAction,
  useRefundSettlement,
} from "../../../hooks";
import { DisconnectionRequest } from "../../../types";
import MeterRemovalFormWithService, {
  MeterRemovalFormRef,
} from "./RemoveMeterForm";
import SettlementView from "./SettlementView";
import { useNavigate, useParams } from "react-router-dom";
import { Switch } from "@shared/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { BUTTON_FIELD_CLASSES, FIELD_CLASSES } from "@shared/utils/constants";
import { normalizeDate } from "@shared/utils/dateUtils";
import { Separator } from "@shared/ui/separator";
import { Checkbox } from "@shared/ui/checkbox";
import { set } from "react-hook-form";
import { Button } from "@shared/ui/button";
import { useGlobalUserProfile } from "@shared/hooks/useGlobalUser";
import { useDateNormalization } from "@shared/hooks/useDateNormalization";
import { usePaymentMode } from "@features/cx/paymentsv2/hooks";

interface SettlementClosureTabProps {
  request: DisconnectionRequest;
  remoteUtilityId: number;
}

const checklistItems = [
  "Final bill generated and sent",
  "Final payment received",
  "Equipment returned/retrieved",
  "Customer notified of closure",
  "Records archived",
  "Account officially closed",
];

const SettlementClosureTab: React.FC<SettlementClosureTabProps> = ({
  request,
  remoteUtilityId,
}) => {
  console.log(request);
  const isFinalized =
    request?.statusDisplay === "COMPLETED" ||
    request?.statusDisplay === "REJECTED";

  if (isFinalized) {
    return (
      <SettlementView request={request} remoteUtilityId={remoteUtilityId} />
    );
  }
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const billStatus = request?.additionalData?.billStatus?.toLowerCase();
  // Checklist state
  const [checked, setChecked] = useState<boolean[]>(
    Array(checklistItems.length).fill(false),
  );
  const [billApproved, setBillApproved] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [customerContactData, setCustomerContactData] = useState({
    // name: "",
    // email: "",
    // phone: "",
    name: request?.consumer?.firstName + request?.consumer?.lastName || "N/A",
    email: request?.consumer?.email || "N/A",
    phone: request?.consumer?.contactNumber || "N/A",
  });

  const [masterAccountAction, setMasterAccountAction] = useState<
    "make_individual" | "move" | null
  >(null);
  const [selectedMasterId, setSelectedMasterId] = useState<number | null>(null);
  const [selectedMaster, setSelectedMaster] = useState<any>(null);

  const isMasterAccount = request?.consumer?.ownershipDisplay === "Master";

  const handleMasterSearch = useCallback(
    async (term: string) => {
      if (term.length < 3) return [];
      try {
        const response = await reconnectionApi.searchMasterConsumers({
          remoteUtilityId,
          searchData: term,
        });
        const results = response?.result || [];
        return results.map((acc: any) => ({
          id: acc.id.toString(),
          label:
            `${acc.first_name || acc.firstName || ""} ${acc.last_name || acc.lastName || ""}`.trim() ||
            "Unknown Consumer",
          description: `Acc: ${acc.consumer_no || acc.consumerNo || "N/A"}`,
          accountNo: acc.consumer_no || acc.consumerNo,
          name: `${acc.first_name || acc.firstName || ""} ${acc.last_name || acc.lastName || ""}`.trim(),
          status: acc.status_display || acc.statusDisplay || "Active",
          email: acc.email || "",
          phone: acc.contact_number || acc.contactNumber || "",
        }));
      } catch (error) {
        console.error("Master search failed:", error);
        return [];
      }
    },
    [remoteUtilityId],
  );

  const handleSelectMaster = useCallback((acc: any) => {
    setSelectedMasterId(parseInt(acc.id));
    setSelectedMaster({
      id: acc.id,
      name: acc.name,
      accountNo: acc.accountNo,
      email: acc.email,
      phone: acc.phone,
      status: acc.status,
    });
  }, []);

  const renderMasterResult = useCallback(
    (result: any) => (
      <div className="p-3 hover:bg-blue-50 cursor-pointer rounded border-l-4 border-transparent hover:border-blue-400 transition-all">
        <div className="font-medium text-gray-900">{result.name}</div>
        <div className="text-sm text-gray-600">Account {result.accountNo}</div>
        {result.status && (
          <div className="text-xs text-gray-500">{result.status}</div>
        )}
      </div>
    ),
    [],
  );

  const masterActionFields: FormField[] = useMemo(
    () => [
      {
        name: "masterAction",
        type: "radio-card",
        className: "col-span-12",
        options: [
          {
            label: "Make All Individual Accounts",
            value: "make_individual",
            description:
              "Each sub-account becomes a standalone account with its own billing, meter, and service agreement. They will no longer be linked to any master account.",
            icon: <UserPlus className="h-5 w-5" />,
          },
          {
            label: "Move to Another Master Account",
            value: "move",
            description:
              "Transfer all sub-accounts to an existing master account. The new master will inherit billing responsibility and shared resources for these accounts.",
            icon: <Users className="h-5 w-5" />,
          },
        ],
        classes: {
          container: "mb-4",
          radioGroup: "grid grid-cols-1 md:grid-cols-2 gap-4 w-full",
          radioCard:
            "flex items-start gap-4 p-5 border-2 rounded-xl cursor-pointer transition-all hover:border-orange-300 bg-white w-full shadow-sm relative",
          radioCardChecked:
            "border-orange-500 bg-orange-50/30 ring-1 ring-orange-500",
          label: "font-bold text-gray-900 text-[15px] leading-tight",
          description:
            "text-[12px] text-gray-500 mt-2 font-medium leading-relaxed",
        },
      },
    ],
    [],
  );

  const masterActionForm = FormService.createForm({
    fields: masterActionFields,
    mode: "onChange",
  });

  const selectedAction = masterActionForm.watch("masterAction");

  useEffect(() => {
    if (selectedAction) {
      setMasterAccountAction(selectedAction as "make_individual" | "move");
    }
  }, [selectedAction]);

  const allChecked = checked.every(Boolean);
  const { mutate: executeMasterAction, isPending: isSavingMasterAction } =
    useMasterAccountAction(request?.consumer?.id);

  const { data: consumerBill } = useConsumerBill({
    consumerId: request?.consumer.id,
    remoteUtilityId: remoteUtilityId,
  });

  console.log("consumerBill", consumerBill);

  const { data: billTemplates } = useBillTemplates(remoteUtilityId);
  const [showBillSummary, setShowBillSummary] = useState(false);
  const [billResponse, setBillResponse] = useState<any>(null);
  const [billGenerated, setBillGenerated] = useState(false);

  console.log("billResponse", billResponse);

  const printBill = usePrintBill();
  const { data: disconnectRequestData, error } = useDisconnectionRequestDetail(
    Number(id),
    String(remoteUtilityId),
  );

  const { mutate: refundAmount } = useRefundRemainingAmountBill();
  const handlePrintClick = async (billId: string) => {
    try {
      printBill.mutateAsync(billId);
    } catch (error) {}
  };
  const { mutateAsync: approveBill, isPending: isApprovingBill } =
    useApproveDisconnectionBill(billResponse?.draftId, Number(id));
  const { mutateAsync: rejectBill, isPending: isRejectingBill } = useRejectBill(
    billResponse?.draftId,
  );

  const { mutate: confirmRefund } = useApproveDisconnectionBill(
    billResponse?.draftId,
    Number(id),
  );
  const handleComplete = async () => {
    setCustomerContactData({
      name: request?.consumer?.firstName + request?.consumer?.lastName || "N/A",
      email: request?.consumer?.email || "N/A",
      phone: request?.consumer?.contactNumber || "N/A",
    });
    setShowApprovalModal(true);
    // const response = await approveBill(); // ✅ Call the mutation
  };

  // const handleFinalApproval = async () => {
  //   try {
  //     if (usingExistingBill) {
  //       setBillApproved(true);
  //     } else {
  //       const response = await approveBill();
  //       setBillApproved(true);
  //     }
  //     setShowApprovalModal(false);
  //   } catch (error) {
  //     console.error("Error approving bill:", error);
  //   }
  // };

  const [sendEBill, setSendEBill] = useState<boolean>(true);
  const [sendPhysicalBill, setSendPhysicalBill] = useState<boolean>(false);
  const handleFinalApproval = async () => {
    try {
      // Build the enabled charges object with all keys
      const enabledCharges = chargeCards.reduce(
        (acc, card) => {
          const baseKey = card.key.replace("include_", "");
          if (card.key === "include_service_charge") {
            acc[card.key] = enabled[card.key] ? ["Security Deposit"] : [];
          } else {
            acc[card.key] = enabled[card.key] ?? true; // ✅ Use the actual enabled state
          }
          acc[`${baseKey}_amount`] = card.amount;
          return acc;
        },
        {} as Record<string, boolean | number | string[]>,
      );

      const payload = {
        send_e_bill: sendEBill,
        send_physical_bill: sendPhysicalBill,
        ...enabledCharges,
      };
      // console.log("debug payload", payload);
      const response = await approveBill(payload);
      setBillApproved(true);
      setShowApprovalModal(false);
    } catch (error) {
      console.error("Error approving bill:", error);
    }
  };

  const handleReject = async () => {
    const response = await rejectBill();
    setShowBillSummary(false);
    setBillGenerated(false);
  };

  const invoiceFields: FormField[] = useMemo(
    () => [
      {
        name: "startDate",
        label: "Start Date",
        type: "datepicker",
        required: true,
        disabled: true,
        placeholder: "Select start date",
      },
      {
        name: "endDate",
        label: "End Date",
        type: "datepicker",
        required: true,
        disabled: request?.additionalData?.billId != null,
        placeholder: "Select end date",
      },
      {
        name: "creditPeriod",
        label: "Payment Due In (Days)",
        type: "number",
        required: false,
        disabled: false,
        placeholder: "Payment Due Date",
      },
      {
        name: "template",
        label: "Select Template",
        type: "select",
        required: true,
        disabled: request?.additionalData?.billId != null,
        placeholder: "Select Template",
        options:
          billTemplates?.map((item) => ({
            label: item.configName,
            value: item.id,
          })) ?? [],
      },
    ],
    [request?.additionalData?.billId, billTemplates],
  );

  const formConfig = useMemo(
    () => ({
      layout: "grid" as const,
      columns: 3,
      spacing: "medium" as const,
      showSubmitButton: false,
      showResetButton: false,
      validateOnMount: false,
      classes: FIELD_CLASSES,
      mode: "onChange" as const,
    }),
    [],
  );

  const formRef = useRef<MeterRemovalFormRef>(null);

  // Modal state
  const [modalStep, setModalStep] = useState(0); // 0: closed, 1: deposit, 2: summary, 3: approve
  const [applyDeposit, setApplyDeposit] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [sendToEmail, setSendToEmail] = useState(true);

  // Service Disconnection state
  const [meterAction, setMeterAction] = useState("unassign");
  const form = FormService.createForm({
    fields: invoiceFields,
    ...formConfig,
  });

  // Watch form values
  const formValues = form.watch();

  const { data: meterReadings } = useMeterReadings({
    remoteUtilityId: remoteUtilityId,
    meterId: consumerBill?.result?.meters,
    startDate: formValues.startDate,
    endDate: formValues.endDate,
  });
  const getMeterDisplayData = () => {
    // If we have meter data in additionalData, use it
    if (
      request?.additionalData?.readingData &&
      request.additionalData.readingData.length > 0
    ) {
      return request.additionalData.readingData.map((reading) => ({
        id: reading.meter,
        meterNumber: reading.meterNumber,
        deviceNo: reading.meterNumber, // Same as meter number in this case
        productCode: reading.productCode,
        meterTypeDisplay: "Photo Meter", // You might want to add this to additionalData
        previousReading: reading.meterReading,
        previousReadingDate: reading.readingDate,
        previousReadingMeterStatus: reading.status,
      }));
    }

    // Fallback to meterReadings from hook
    return meterReadings || [];
  };

  const { mutateAsync: updateClosure, isPending: isClosureLoading } =
    useUpdateDisconnectionRequest(request?.id);
  const { mutateAsync: updateDisconnection, isPending: isUpdateLoading } =
    useUpdateDisconnection(request?.id);

  const rawBillData = consumerBill?.result?.billData;

  const billData = Array.isArray(rawBillData) ? rawBillData[0] : rawBillData;
  const { mutateAsync: submitConsumer, isPending: isSubmitting } =
    useSubmitConsumerSelection();

  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const [meterReadingsData, setMeterReadingsData] = useState<
    {
      meter: number;
      meter_number: string;
      product_code: string;
      meter_reading: number | null;
      reading_source: number;
      reading_date: string;
      status: number;
    }[]
  >([]);

  console.log("request", request);
  useEffect(() => {
    if (!consumerBill?.result) return;

    const today = new Date().toISOString().split("T")[0];
    const billValues = {
      startDate:
        normalizeWithUserFormat(billData?.invoiceStartDate) ||
        normalizeWithUserFormat(consumerBill?.result?.activateDate),
      endDate: today,
      creditPeriod: billData?.creditPeriod,
    };
    FormService.populateForm(form, billValues, invoiceFields);
  }, [
    billData?.invoiceStartDate,
    billData?.creditPeriod,
    consumerBill?.result?.activateDate,
  ]);

  useEffect(() => {
    if (meterReadings && meterReadings.length > 0) {
      const initialReadingsData = meterReadings.map((meter) => ({
        meter: meter.id,
        meter_number: meter.meterNumber,
        product_code: meter.productCode,
        meter_reading: meter.previousReading,
        reading_source: 1, // Default value
        reading_date: meter.previousReadingDate || "",
        status: meter.previousReadingMeterStatus, // Default value
      }));
      setMeterReadingsData(initialReadingsData);
    }
  }, [meterReadings]);

  useEffect(() => {
    // First check if there's a bill in request.additionalData
    if (request?.additionalData?.billId) {
      // Create bill response object from additionalData
      const existingBillResponse = {
        // Core bill identification
        billId: request.additionalData.billId,
        billNumber: request.additionalData.billNumber,
        billDraftId: request.additionalData.billDraftId,

        // Invoice details
        invoicePeriod: request.additionalData.invoicePeriod,
        invoiceStartDate: request.additionalData.invoiceStartDate,
        invoiceEndDate: request.additionalData.invoiceEndDate,

        // Financial amounts
        billAmount: request.additionalData.billAmount || 0,
        totalOutstanding: request.additionalData.balanceBeforeCharges,
        netAmountDue: request.additionalData.netAmountDue,
        securityDeposit: request.additionalData.securityDeposit || 0,
        totalPaymentReceived: request.additionalData.totalPaymentReceived || 0,

        // Dates
        dueDate: request.additionalData.dueDate,
        createdDate: request.additionalData.createdDate,
        lastPayDate: request.additionalData.lastPayDate,

        // Configuration and flags
        billConfigId: request.additionalData.billConfigId,
        billConfigType: request.additionalData.billConfigType,
        billStatus: request.additionalData.billStatus,
        includeCredit: request.additionalData.includeCredit,
        isDepositApplied: request.additionalData.isDepositApplied,
        excludeSecurityDeposit: request.additionalData.excludeSecurityDeposit,
        excludeOutstandingBalance:
          request.additionalData.excludeOutstandingBalance,

        // Request and workflow
        requestId: request.additionalData.requestId,
        additionlaOutstanding: request.additionalData.totalOutstanding,

        preferredTimeSlot: request.additionalData.preferredTimeSlot,

        // Reading data (if available)
        readingData: request.additionalData.readingData || [],

        // Consumer info (from parent request object for consistency)
        consumerId: request?.consumer?.id,
        consumerNo: request?.consumer?.consumerNo,
        firstName: request?.consumer?.firstName,
        lastName: request?.consumer?.lastName,
        contactNumber: request?.consumer?.contactNumber,

        // Meter information (from reading_data if available)
        meterInfo:
          request.additionalData.readingData?.map((reading) => ({
            meter: reading.meter,
            meterNumber: reading.meterNumber,
            productCode: reading.productCode,
            meterReading: reading.meterReading,
            readingDate: reading.readingDate,
            readingSource: reading.readingSource,
            status: reading.status,
          })) || [],
      };

      setBillResponse(existingBillResponse);
      setShowBillSummary(true);
      setBillGenerated(true); // Mark as generated since it exists
      return; // Exit early, don't check consumerBill
    }

    // Original logic for consumerBill (fallback)
    if (
      consumerBill?.result?.billData &&
      consumerBill.result.billData.length > 0
    ) {
      const mostRecentBill = consumerBill.result.billData[0];
      setShowBillSummary(true);
    }
  }, [consumerBill, request?.additionalData]);
  // useEffect(() => {
  //   if (
  //     consumerBill?.result?.billData &&
  //     consumerBill.result.billData.length > 0
  //   ) {
  //     // Use the most recent bill (first one in the array)
  //     const mostRecentBill = consumerBill.result.billData[0];
  //     // setSelectedExistingBill(mostRecentBill);
  //     // setUsingExistingBill(true);
  //     setShowBillSummary(true); // Automatically show the bill summary
  //   }
  // }, [consumerBill]);

  const getBillDisplayData = () => {
    console.log("billResponse in display", billResponse);
    const firstBill = consumerBill?.result?.billData?.[0];
    const activeData = billResponse || firstBill;
    if (!activeData) return null;

    const invoiceNo = firstBill?.invoiceNo || "N/A";

    const billPeriod = firstBill?.billPeriod || "N/A";

    const billAmount = Number(firstBill?.billAmount ?? 0);

    const totalOutstanding = Number(firstBill?.outstandingBalance ?? 0);

    const securityDeposit =
      activeData?.securityDeposit ?? firstBill?.securityDeposit ?? 0;

    const additionlaOutstanding = billAmount + totalOutstanding;

    return {
      ...activeData,
      invoiceNo,
      billPeriod,
      billNumber: invoiceNo,
      invoicePeriod: billPeriod,
      billAmount,
      totalOutstanding,
      securityDeposit,
      additionlaOutstanding,
    };
  };
  const getBillSource = () => {
    if (request?.additionalData?.billId) {
      return "additionalData";
    }
    if (billResponse && billGenerated) {
      return "newlyGenerated";
    }

    return "none";
  };

  const handleMeterReadingChange = (
    meterId: number,
    field: "meter_reading" | "reading_date",
    value: string | number,
  ) => {
    setMeterReadingsData((prev) =>
      prev.map((meter) =>
        meter.meter === meterId ? { ...meter, [field]: value } : meter,
      ),
    );
  };

  const getMeterReadingsForSubmission = () => {
    return {
      reading_data: meterReadingsData,
    };
  };

  // Handlers
  const handleGenerateBill = async () => {
    const isValid = await FormService.validateForm(form);
    if (!isValid) {
      return;
    }
    const meterReadingsSubmissionData = getMeterReadingsForSubmission();
    const selectedConfig = billTemplates.find(
      (config) => config.id?.toString() === formValues.template?.toString(),
    );
    const payload = {
      remote_utility_id: REMOTE_UTILITY_ID,
      start_date: formValues.startDate,
      end_date: formValues.endDate,
      credit_period: formValues?.creditPeriod,
      premise: request?.territoryData?.billing?.premise,
      bill_config_id: formValues.template,
      bill_config_type: selectedConfig.source,
      requestId: id,
      consumer_data: {
        id: request.consumer.id,
        consumer_no: request.consumer.consumerNo,
        status: request.consumer.status,
        is_e_bill: consumerBill?.result?.isEBill,
        is_papered_bill: consumerBill?.result?.isPaperedBill,
      },
      bill_data: {
        remote_consumer_number: request.consumer.consumerNo,
        remote_utility_id: REMOTE_UTILITY_ID,
        consumer_data: {
          id: request.consumer.id,
          consumer_no: request.consumer.consumerNo,
          status: request.consumer.status,
          is_e_bill: consumerBill?.result?.isEBill,
          is_papered_bill: consumerBill?.result?.isPaperedBill,
        },
        invoice_start_date: formValues.startDate,
        invoice_end_date: formValues.endDate,
        bill_config_id: formValues.template,
        bill_config_type: selectedConfig.source,
        ...enabled,
        include_service_charge: enabled.include_service_charge
          ? ["Security Deposit"]
          : [],
      },
      ...meterReadingsSubmissionData,
      bill_generate: "true",
      request_id: request.id,
      step: {
        step_working_on: 2,
        total_steps: 3,
      },
    };
    console.log("payload", payload);

    const response = await submitConsumer(payload);
    if (response) {
      setShowBillSummary(true);
      setBillResponse(response);
      setBillGenerated(true);
    }
  };
  const currency = useCurrency();
  const { normalizeWithUserFormat } = useDateNormalization();

  const handleCompleteClosure = async () => {
    const additionalData = {
      is_final_bill_generate_and_send: checked[0], // Final bill generated and sent
      is_payment_received: checked[1], // Final payment received
      is_equipment_received: checked[2], // Equipment returned/retrieved
      is_customer_notified: checked[3], // Customer notified of closure
      is_records_archieved: checked[4], // Records archived
      is_acc_closed: checked[5], // Account officially closed
      is_unassigned_meter: meterAction === "unassign" ? 1 : 0,
      master_account_action: masterAccountAction,
      new_master_account_id: selectedMasterId,
    };

    if (meterAction === "unassign") {
      const payload = {
        status: 4,
        is_unassigned_meter: 1,
        additional_data: additionalData,
      };
      await updateDisconnection({
        requestId: request.id,
        payload: payload,
      });
    } else {
      const isValid = await formRef.current?.validateForm();
      if (isValid) {
        const formData = formRef.current?.getFormValues();
        const remote_meter_ids = Object.values(
          formData.selected_device_numbers || {},
        )
          .filter((device: any) => device && device.id)
          .map((device: any) => Number(device.id));

        const payload = {
          remote_workorder_id: formData.selected_work_order.id,
          remote_meter_ids,
          additional_data: additionalData,
        };
        await updateDisconnection({
          requestId: request.id,
          payload: payload,
        });
      }
    }
  };

  const getMeterBackground = (productCode: string) => {
    const code = productCode.toLowerCase();
    if (code.includes("water")) {
      return "bg-cyan-50";
    } else if (code.includes("electric") || code.includes("elctrciity")) {
      return "bg-blue-50";
    }
    return "bg-gray-50";
  };

  const getMeterIcon = (productCode: string) => {
    const code = productCode.toLowerCase();
    if (code.includes("water")) {
      return <Droplet className="h-5 w-5 text-cyan-500" />;
    } else if (code.includes("electric") || code.includes("elctrciity")) {
      return <Zap className="h-5 w-5 text-blue-500" />;
    }
    // Default icon for other types
    return <Zap className="h-5 w-5 text-gray-500" />;
  };

  const utilityServiceList =
    request?.consumer?.consumerMappingData?.map(
      (item) => item.utilityService,
    ) || [];

  const getSafeAmount = (val: any): number => {
    if (Array.isArray(val)) {
      return val.reduce(
        (sum, item) => sum + (Number(item?.fee || item?.amount || item) || 0),
        0,
      );
    }
    if (typeof val === "object" && val !== null) {
      return Number(val?.fee || val?.amount || 0);
    }
    return Number(val) || 0;
  };

  const [submissionLoading, setSubmissionLoading] = useState(false);
  const chargeCards = useMemo(
    () => [
      {
        key: "include_service_charge",
        label: "Security Deposit Adjustment",
        amount: getSafeAmount(billData?.securityDeposit),
      },
      {
        key: "include_credit",
        label: "Credit Amount Adjustment",
        amount: getSafeAmount(consumerBill?.result?.totalCreditAmount),
      },
      {
        key: "include_payment_agreement",
        label: "Payment Agreement ",
        amount: getSafeAmount(consumerBill?.result?.remainingAgreementAmount),
      },
    ],
    [
      billData?.securityDeposit,
      consumerBill?.result?.totalCreditAmount,
      consumerBill?.result?.remainingAgreementAmount,
    ],
  );

  const [enabled, setEnabled] = useState<Record<string, boolean>>({});

  const handleToggle = (key: string, value: boolean) => {
    setEnabled((prev) => ({ ...prev, [key]: value }));
  };
  // Modal content
  const renderModalContent = () => {
    if (modalStep === 2) {
      const displayData = getBillDisplayData();
      console.log("displayData", displayData);
      return (
        <div className="space-y-4">
          <div className="font-bold text-lg mb-2 text-purple-700">
            Final Bill Summary
          </div>
          <div className="bg-purple-50 rounded p-4 mb-2">
            <div className="flex justify-between mb-2">
              <span>Invoice No:</span>
              <span className="font-semibold">
                {displayData?.invoiceNo || displayData?.billNumber}
              </span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Billing Period:</span>
              <span>
                {displayData?.billPeriod || displayData?.invoicePeriod}
              </span>
            </div>
            <div className="font-semibold mb-2">Bill Details</div>
            <div className="flex justify-between">
              <span>Billed Amount</span>
              <span>
                {currency} {displayData?.billAmount}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Outstanding Amount</span>
              <span>
                {currency} {displayData?.totalOutstanding}
              </span>
            </div>
            {applyDeposit && displayData?.securityDeposit > 0 && (
              <div className="flex justify-between text-green-700">
                <span>Security Deposit Applied</span>
                <span>
                  - {currency} {displayData?.securityDeposit}
                </span>
              </div>
            )}
            <div className="flex justify-between font-bold text-lg mt-2">
              <span>Final Amount Due</span>
              <span className="text-red-600">
                {currency} {displayData?.additionlaOutstanding}
              </span>
            </div>
          </div>
          <div className="bg-white rounded p-4">
            <div className="font-semibold mb-2">
              Security Deposit Information
            </div>
            <div className="flex justify-between">
              <span>Original Deposit</span>
              <span>
                {currency} {displayData?.securityDeposit}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Applied to Bill</span>
              <span>{enabled?.include_service_charge ? "Yes" : "No"}</span>
            </div>
          </div>
          <div className="flex gap-4 mt-4">
            <button
              className="flex-1 py-2 rounded bg-blue-600 text-white font-semibold"
              onClick={() => setModalStep(1)}
            >
              Back
            </button>
            <button
              className="flex-1 py-2 rounded bg-green-600 text-white font-semibold"
              onClick={() => handleFinalApproval()}
              disabled={isApprovingBill}
            >
              Approve
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    setEnabled((prev) => {
      const newEnabled = chargeCards.reduce(
        (acc, { key }) => {
          // Preserve existing value if it exists, otherwise default to true
          acc[key] = prev[key] !== undefined ? prev[key] : true;
          return acc;
        },
        {} as Record<string, boolean>,
      );

      // Only update if actually changed
      const prevKeys = Object.keys(prev).sort();
      const newKeys = Object.keys(newEnabled).sort();

      if (
        prevKeys.length !== newKeys.length ||
        prevKeys.some((key, i) => key !== newKeys[i]) ||
        prevKeys.some((key) => prev[key] !== newEnabled[key])
      ) {
        return newEnabled;
      }
      return prev;
    });
  }, [chargeCards]);

  const isSecurityDepositIncludedInLastBill = (
    disconnectRequestData?.additionalData as any
  )?.refundData?.includeServiceCharge;

  const securityDepositAmount =
    (disconnectRequestData?.additionalData as any)?.refundData
      ?.serviceChargeAmount ??
    (billData?.securityDeposit != null
      ? getSafeAmount(billData?.securityDeposit)
      : 0);

  const totalCreditAmount = Number(
    disconnectRequestData?.refundSettlement?.remainingCreditRefund || 0,
  );

  const showSecurityDepositRefund = !isSecurityDepositIncludedInLastBill;

  const totalRefundAmount =
    (showSecurityDepositRefund ? Number(securityDepositAmount || 0) : 0) +
    Number(totalCreditAmount || 0);

  const [isRefundConfirmed, setIsRefundConfirmed] = useState(
    (request?.additionalData as any)?.refundData?.confirmTotalRefund || false,
  );

  const handleConfirmRefund = (checked: boolean | string) => {
    setIsRefundConfirmed(!!checked);
  };

  const { data: paymentModesData } = usePaymentMode(Number(remoteUtilityId));
  const { mutate: submitRefundSettlement, isPending } = useRefundSettlement();
  const isSubmittingRefund = isPending;
  const paymentModeList = Array.isArray(paymentModesData)
    ? paymentModesData
    : Array.isArray(paymentModesData?.result)
      ? paymentModesData.result
      : [];

  const dynamicRefundMethodOptions = paymentModeList.map((m: any) => ({
    label: m.name,
    value: m.name,
  }));

  const dynamicCustomFields: FormField[] = paymentModeList.flatMap((m: any) => {
    const fields = m.customFields || [];
    return fields.map((cf: any, index: number) => {
      const isLastOdd = fields.length % 2 !== 0 && index === fields.length - 1;
      const isAmountField =
        cf.key === "amount" || cf.label?.toLowerCase().includes("amount");

      return {
        name: cf.key || cf.id?.toString(),
        label: isAmountField ? "Total Refund Amount" : cf.label,
        type: cf.fieldType === "date" ? "datepicker" : cf.fieldType || "text",
        required: true,
        disabled: isAmountField,
        placeholder: isAmountField
          ? "Total Refund Amount"
          : cf.placeholder ||
            (cf.fieldType === "date"
              ? "Select date"
              : `Enter ${cf.label?.toLowerCase()}`),
        showWhen: (values: any) =>
          values.refundMode === m.name &&
          (values.disposition === "refundToCustomer" || !values.disposition),
        className: isLastOdd ? "col-span-12" : "col-span-12 md:col-span-6",
      };
    });
  });

  const refundFields: FormField[] = [
    {
      name: "disposition",
      label: "",
      type: "radio-card",
      required: true,
      defaultValue: "refundToCustomer",
      className: "col-span-12",
      options: [
        {
          label: "Refund to Customer",
          value: "refundToCustomer",
          description:
            "Return the amount to the customer via a chosen payment method. A refund entry will appear in Payments → Refunds.",
        },
        {
          label: "Keep on Account",
          value: "keepOnAccount",
          description:
            "Retain the amount as a credit balance on this account. A credit note will be generated — the amount will not be refunded to the customer.",
        },
      ],
      classes: {
        container: "mb-2",
        radioGroup: "grid grid-cols-1 md:grid-cols-2 gap-3 w-full",
        radioCard:
          "flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all hover:border-blue-300 bg-white w-full h-full",
        radioCardChecked:
          "border-blue-500 bg-blue-50/70 shadow-sm ring-1 ring-blue-500",
        label: "font-semibold text-slate-800 text-sm leading-tight",
        description:
          "text-xs text-slate-500 mt-0.5 font-medium leading-relaxed",
      },
    },
    {
      name: "refundMode",
      label: "Refund Mode",
      type: "select",
      required: true,
      showWhen: (values) =>
        values.disposition === "refundToCustomer" || !values.disposition,
      options: dynamicRefundMethodOptions,
      placeholder: "Select refund mode",
      className: "col-span-12",
    },
    ...dynamicCustomFields,
    {
      name: "creditNoteInfo",
      type: "custom",
      showWhen: (values) => values.disposition === "keepOnAccount",
      className: "col-span-12 mt-2",
      customRender: () => (
        <div className="space-y-3">
          <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-4 space-y-2.5">
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-500 font-medium">Credit Amount</span>
              <span className="font-semibold text-slate-800">
                {currency} {Number(totalRefundAmount).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-500 font-medium">Credit Note</span>
              <span className="font-medium text-slate-600">
                Auto-generated on disconnection
              </span>
            </div>
            <div className="mt-2 rounded-md bg-purple-100 border border-purple-200 px-3 py-2">
              <p className="text-xs text-purple-700 font-medium">
                A Credit Note of {currency}{" "}
                {Number(totalRefundAmount).toFixed(2)} will be generated and
                visible in Payments → Credit Notes
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      name: "creditNoteNote",
      label: "Note",
      type: "textarea",
      placeholder:
        "Add a note for this credit note (e.g. reason for keeping on account)…",
      showWhen: (values) => values.disposition === "keepOnAccount",
      className: "col-span-12",
    },
    {
      name: "confirmTotalRefund",
      type: "custom",
      className: "col-span-12 mt-4",
      customRender: (field, form) => {
        const disposition = form.watch("disposition");
        const checked = form.watch("confirmTotalRefund");
        return (
          <div className="flex items-start gap-3 pt-1">
            <Checkbox
              id="confirm-refund"
              checked={checked}
              onCheckedChange={(c) => {
                form.setValue("confirmTotalRefund", !!c, {
                  shouldValidate: true,
                });
                handleConfirmRefund(!!c);
              }}
              className="mt-0.5"
            />
            <label
              htmlFor="confirm-refund"
              className="text-sm leading-relaxed text-slate-600 cursor-pointer select-none"
            >
              {disposition === "refundToCustomer" ? (
                <>
                  <span className="font-semibold text-slate-800">
                    Confirm Total Refund
                  </span>{" "}
                  — By checking this box, I confirm that the total refund amount
                  of{" "}
                  <span className="font-semibold">
                    {currency}
                    {Number(totalRefundAmount).toFixed(2)}
                  </span>{" "}
                  has been successfully refunded to the customer.
                </>
              ) : (
                <>
                  <span className="font-semibold text-slate-800">
                    Confirm Credit Note
                  </span>{" "}
                  — By checking this box, I confirm that{" "}
                  <span className="font-semibold">
                    {currency}
                    {Number(totalRefundAmount).toFixed(2)}
                  </span>{" "}
                  will be retained as a credit balance on this account and a
                  credit note will be generated.
                </>
              )}
            </label>
          </div>
        );
      },
    },
  ];

  const refundSettlementData = request?.additionalData?.refundSettlementData;

  const defaultRefundValues: any = useMemo(() => {
    const rawExtraData =
      refundSettlementData?.extraData ||
      (refundSettlementData as any)?.extra_data ||
      {};

    const extraData: any = {};
    Object.keys(rawExtraData).forEach((camelKey) => {
      let value = rawExtraData[camelKey];

      // Convert camelCase key → snake_case to match cf.key form field names
      const snakeKey = camelKey.replace(
        /([A-Z])/g,
        (g) => `_${g.toLowerCase()}`,
      );

      // Normalize date values
      if (camelKey.toLowerCase().includes("date") && value) {
        try {
          value = normalizeDate(String(value));
        } catch {
          // keep original
        }
      }

      // Store both camelCase and snake_case so either form field naming works
      extraData[camelKey] = value;
      extraData[snakeKey] = value;
    });

    const values: any = {
      disposition: "refundToCustomer",
      refundMode: "",
      chequeNumber: "",
      chequeDate: "",
      chequeBankName: "",
      chequePayeeName: "",
      creditNoteNote: "",
      confirmTotalRefund:
        request?.additionalData?.refundSettlementData?.extraData
          ?.confirmCreditNote || false,
    };

    if (refundSettlementData) {
      if (
        refundSettlementData.disposition === "refundToCustomer" ||
        refundSettlementData.disposition === "refund_to_customer"
      ) {
        values.disposition = "refundToCustomer";
      } else if (
        refundSettlementData.disposition === "keepOnAccount" ||
        refundSettlementData.disposition === "keep_on_account"
      ) {
        values.disposition = "keepOnAccount";
      }

      if (refundSettlementData.refundMode || refundSettlementData.refund_mode) {
        values.refundMode =
          refundSettlementData.refundMode || refundSettlementData.refund_mode;
      }
      if (refundSettlementData.creditNoteNote) {
        values.creditNoteNote = refundSettlementData.creditNoteNote;
      }
    }

    // Spread extraData AFTER base values — populates dynamic fields (paymentAmount, paymentDate, etc.)
    Object.assign(values, extraData);

    // Override amount fields with totalRefundAmount (auto-calculated) for all modes
    paymentModeList.forEach((m: any) => {
      m.customFields?.forEach((cf: any) => {
        const key = cf.key || cf.id?.toString();
        if (cf.key === "amount" || cf.label?.toLowerCase().includes("amount")) {
          values[key] = Number(totalRefundAmount).toFixed(2);
        }
      });
    });

    return values;
  }, [refundSettlementData, request, paymentModeList, totalRefundAmount]);

  const refundForm = FormService.createForm({
    fields: refundFields,
    defaultValues: defaultRefundValues,
    mode: "onChange",
  });

  useEffect(() => {
    if (refundSettlementData) {
      refundForm.reset(defaultRefundValues);
    }
  }, [refundSettlementData, defaultRefundValues, refundForm]);

  const handleSubmitRefund = async (data: any) => {
    const payload: any = {
      requestId: request?.id,
      disposition:
        data.disposition === "refundToCustomer"
          ? "refund_to_customer"
          : "keep_on_account",
      securityDepositAmount: Number(securityDepositAmount || 0).toFixed(2),
      creditAmount: Number(totalCreditAmount || 0).toFixed(2),
      totalRefundAmount: Number(totalRefundAmount || 0).toFixed(2),
      extra_data: {
        confirm_credit_note: !!data.confirmTotalRefund,
      },
    };

    if (data.disposition === "refundToCustomer") {
      payload.refund_mode = data.refundMode;

      const activeMode = paymentModeList.find(
        (m: any) => m.name === data.refundMode,
      );
      const activeDynamicFieldKeys = (activeMode?.customFields || []).map(
        (cf: any) => cf.key || cf.id?.toString(),
      );

      activeDynamicFieldKeys.forEach((key: string) => {
        if (data[key] !== undefined) {
          payload.extra_data[key] = data[key];
        }
      });
    } else if (data.disposition === "keepOnAccount") {
      if (data.creditNoteNote) {
        payload.creditNoteNote = data.creditNoteNote;
      }
    }

    submitRefundSettlement(payload, {
      onSuccess: () => {
        handleConfirmRefund(true);
      },
    });
  };

  const handleCancelRefund = () => {
    refundForm.reset({
      disposition: "refundToCustomer",
      refundMode: "",
      chequeNumber: "",
      chequeDate: "",
      chequeBankName: "",
      chequePayeeName: "",
      creditNoteNote: "",
      confirmTotalRefund: false,
    });
    handleConfirmRefund(false);
  };
  const refundTableData = [];

  if (showSecurityDepositRefund && Number(securityDepositAmount || 0) > 0) {
    refundTableData.push({
      id: "security_deposit",
      description: "Security Deposit Refund",
      amount:
        billData?.securityDeposit != null
          ? getSafeAmount(billData?.securityDeposit)
          : "NA",
    });
  }

  if (Number(totalCreditAmount || 0) > 0) {
    refundTableData.push({
      id: "remaining_credit",
      description: "Remaining Credit Refund",
      amount: Number(totalCreditAmount).toFixed(2),
    });
  }

  refundTableData.push({
    id: "total_refund",
    description: "Total Refund Amount",
    amount: Number(totalRefundAmount).toFixed(2),
    isTotal: true,
  });

  const refundTableColumns: TableColumn[] = [
    {
      header: "Description",
      key: "description",
      render: (value: any, row: any) =>
        row.isTotal ? (
          <span className="font-bold text-slate-800">{row.description}</span>
        ) : (
          <span className="text-slate-600">{row.description}</span>
        ),
    },
    {
      header: "Amount",
      key: "amount",
      render: (value: any, row: any) =>
        row.isTotal ? (
          <div className="text-right font-bold text-green-600 text-base">
            {currency} {row.amount}
          </div>
        ) : (
          <div className="text-right font-medium">
            {currency} {row.amount}
          </div>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Master Account Note and Actions */}
      {isMasterAccount && (
        <div className="bg-[#FFF9F0] border border-[#FFE4B5] rounded-2xl p-6 shadow-sm overflow-hidden relative">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="bg-[#F59E0B] p-2.5 rounded-xl">
                <AlertCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#92400E]">
                  This is a Master Account
                </h3>
                <p className="text-sm text-[#B45309] font-medium mt-0.5">
                  Account #{request?.consumer?.consumerNo} •{" "}
                  {request?.consumer?.name} •{" "}
                  {request?.consumer?.consumerMappingData?.length || 0} linked
                  sub-accounts
                </p>
              </div>
            </div>
            <Badge className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-black px-4 py-1 rounded-full text-[10px] tracking-widest">
              {request?.consumer?.ownershipDisplay}
            </Badge>
          </div>

          <div className="bg-[#FEF3C7]/50 border-l-4 border-[#F59E0B] p-5 rounded-r-xl mb-8">
            <p className="text-[13px] text-[#92400E] leading-relaxed">
              <span className="font-bold">Important:</span> Disconnecting this
              master account will directly affect{" "}
              <span className="font-bold">
                {request?.consumer?.consumerMappingData?.length || 0}{" "}
                sub-accounts
              </span>{" "}
              currently linked to it. All billing, service agreements, and
              shared resources tied to these sub-accounts will be impacted.
              Please choose how to handle the sub-accounts before proceeding.
            </p>
          </div>

          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-5 w-5 rounded-full border-2 border-[#F59E0B] flex items-center justify-center">
                <div className="h-2 w-2 rounded-full bg-[#F59E0B]"></div>
              </div>
              <h4 className="font-bold text-[#92400E] text-sm">
                How would you like to handle the sub-accounts?
              </h4>
            </div>

            <DynamicForm
              fields={masterActionFields}
              form={masterActionForm}
              config={{
                ...formConfig,
                classes: {
                  ...FIELD_CLASSES,
                },
              }}
            />

            {/* Conditional Search for Moving to Master Account */}
            {selectedAction === "move" && (
              <div className="animate-in fade-in slide-in-from-top-4 duration-300 mt-4">
                {!selectedMasterId ? (
                  <div className="bg-white p-6 rounded-2xl border border-orange-200 shadow-sm">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="bg-orange-100 p-2 rounded-lg">
                        <Users className="h-5 w-5 text-orange-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-800">
                        New Master Account Search
                      </h3>
                    </div>

                    <div className="relative">
                      <SearchInput
                        placeholder="Search by account number, name or address..."
                        onSearch={handleMasterSearch}
                        onSelect={handleSelectMaster}
                        renderResult={renderMasterResult}
                        minSearchLength={3}
                        className="w-full h-12"
                        debounceMs={300}
                      />
                      <p className="text-[11px] text-gray-500 mt-3 flex items-center gap-1.5 font-medium">
                        <FileText className="h-3.5 w-3.5 text-orange-400" />
                        Enter at least 3 characters to search available master
                        accounts
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white p-5 rounded-2xl border border-green-200 shadow-md animate-in zoom-in-95 duration-300">
                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-50">
                      <div className="flex items-center gap-2.5">
                        <div className="bg-green-100 p-1.5 rounded-full">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        </div>
                        <h4 className="font-bold text-gray-800 text-sm">
                          Target Master Account
                        </h4>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedMasterId(null);
                          setSelectedMaster(null);
                        }}
                        className="h-8 text-[10px] font-black tracking-widest text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                      >
                        Change
                      </Button>
                    </div>

                    {selectedMaster && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-6">
                        <div className="space-y-1">
                          <p className="text-[10px] font-black text-gray-400 tracking-wider">
                            Account Number
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            {selectedMaster.accountNo}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-[10px] font-black text-gray-400 tracking-wider">
                            Customer Name
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            {selectedMaster.name}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-[10px] font-black text-gray-400 tracking-wider">
                            Account Status
                          </p>
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-200 border-none font-bold text-[10px]">
                            {selectedMaster.status}
                          </Badge>
                        </div>
                        <div className="space-y-1">
                          <p className="text-[10px] font-black text-gray-400 tracking-wider">
                            Email Address
                          </p>
                          <p className="text-sm font-bold text-gray-700 line-clamp-1">
                            {selectedMaster.email || "No email provided"}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-[10px] font-black text-gray-400 tracking-wider">
                            Phone Number
                          </p>
                          <p className="text-sm font-bold text-gray-700 line-clamp-1">
                            {selectedMaster.phone || "No phone provided"}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end">
              <Button
                className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-bold rounded-xl flex items-center gap-2"
                disabled={
                  !selectedAction ||
                  (selectedAction === "move" && !selectedMasterId) ||
                  isSavingMasterAction
                }
                onClick={() => {
                  executeMasterAction({
                    master_account_action: selectedAction as
                      | "make_individual"
                      | "move",
                    new_master_account_id:
                      selectedAction === "move"
                        ? Number(selectedMasterId)
                        : undefined,
                  });
                }}
              >
                {isSavingMasterAction ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <CheckCircle2 className="h-5 w-5" />
                )}
                Confirm & Proceed with Disconnection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Financial Settlement */}
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="font-bold text-xl mb-4">Financial Settlement</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {/* Outstanding Balance */}
          <div className="bg-red-50 rounded p-4">
            <div className="font-semibold text-lg text-red-700 flex items-center gap-2 mb-2">
              Outstanding Balance
            </div>
            <div className="flex justify-between">
              <span>Total Outstanding</span>
              <span className="font-bold text-red-600 text-lg">
                {currency} {billData?.outstandingBalance ?? "NA"}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Due Date</span>
              <span>{billData?.dueDate ?? "NA"}</span>
            </div>
          </div>
          {/* Payment History */}
          <div className="bg-blue-50 rounded p-4">
            <div className="font-semibold text-lg text-blue-700 mb-2">
              Payment History
            </div>
            <div className="flex justify-between">
              <span>Last Payment</span>
              <span>
                {currency} {billData?.lastPayAmount ?? "NA"}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Payment Date</span>
              <span>{billData?.lastPaymentDate ?? "NA"}</span>
            </div>
          </div>
          {/* Security Deposit */}
          <div className="bg-green-50 rounded p-4">
            <div className="font-semibold text-lg text-green-700 flex items-center gap-2 mb-2">
              <CreditCard className="h-5 w-5" /> Security Deposit
            </div>
            <div className="flex justify-between">
              <span>Deposit Amount</span>
              <span className="font-bold text-green-700">
                {currency}{" "}
                {billData?.securityDeposit != null
                  ? getSafeAmount(billData?.securityDeposit)
                  : "NA"}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Deposit Date</span>
              <span>{billData?.depositDate ?? "NA"}</span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={applyDeposit}
                onChange={() => setApplyDeposit((v) => !v)}
              />
              <span>Use in bill</span>
            </div>
          </div>
          {/* Net Settlement */}
          <div className="bg-yellow-50 rounded p-4">
            <div className="font-semibold text-lg text-yellow-700 mb-2">
              Net Settlement
            </div>
            <div className="flex justify-between">
              <span>Outstanding</span>
              <span className="text-red-600">
                {currency} {billData?.outstandingBalance || "NA"}
              </span>
            </div>
            {applyDeposit && (
              <div className="flex justify-between">
                <span>Deposit Amount</span>
                <span className="text-red-600">
                  {`${currency} ${Number(billData?.securityDeposit || 0)}`}
                </span>
              </div>
            )}

            <div className="flex justify-between font-bold">
              <span>Net Amount</span>
              <span className="text-red-600">
                {currency}{" "}
                {applyDeposit
                  ? Number(billData?.outstandingBalance || 0) -
                    Number(billData?.securityDeposit || 0)
                  : Number(billData?.outstandingBalance || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {chargeCards.map(({ key, label, amount }) => (
          <Card key={key} className="rounded-xl">
            <CardContent className="flex items-center justify-between p-4">
              <div>
                <p className="text-lg font-semibold">{label}</p>
                <p className="text-sm text-foreground">
                  {currency} {amount}
                </p>
              </div>

              <Switch
                checked={!!enabled[key]}
                onCheckedChange={(val) => handleToggle(key, val)}
              />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="font-bold text-xl mb-4 flex items-center gap-2">
          <span className="text-sm">📄</span> Invoice Details
        </div>

        {/* DynamicForm handles its own grid layout */}
        <DynamicForm
          fields={invoiceFields}
          form={form}
          config={formConfig}
          gridColumns={4}
          responsiveColumns={{
            sm: 1,
            md: 2,
            lg: 4,
          }}
          onSubmit={function (data: any): void | Promise<void> {
            throw new Error("Function not implemented.");
          }}
        />
      </div>
      {/* Meter Details */}
      {form.watch("startDate") && form.watch("endDate") && (
        <div className="bg-white rounded-lg shadow border p-6">
          <div className="font-bold text-xl mb-4 flex items-center gap-2">
            <Layers className="h-5 w-5" /> Meter Details
          </div>
          <div className="flex flex-col gap-4">
            {getMeterDisplayData() && getMeterDisplayData().length > 0 ? (
              getMeterDisplayData().map((meter, index) => (
                <div
                  key={meter.id}
                  className={`rounded p-4 ${getMeterBackground(
                    meter.productCode,
                  )}`}
                >
                  <div className="font-semibold text-base flex items-center gap-2 mb-2">
                    {getMeterIcon(meter.productCode)} {meter.productCode} Meter
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500">
                        Meter Number
                      </label>
                      <input
                        className="w-full border rounded px-2 py-1"
                        value={meter.meterNumber}
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">
                        Device Number
                      </label>
                      <input
                        className="w-full border rounded px-2 py-1"
                        value={meter.deviceNo}
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">
                        Meter Type
                      </label>
                      <input
                        className="w-full border rounded px-2 py-1"
                        value={meter.meterTypeDisplay}
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">
                        Last Reading
                      </label>
                      <input
                        className="w-full border rounded px-2 py-1 bg-white"
                        type="number"
                        value={meterReadingsData[index]?.meter_reading || ""}
                        onChange={(e) =>
                          handleMeterReadingChange(
                            meter.id,
                            "meter_reading",
                            e.target.value ? parseFloat(e.target.value) : null,
                          )
                        }
                        placeholder="Enter reading"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">
                        Reading Date
                      </label>
                      <input
                        className="w-full border rounded px-2 py-1 bg-white"
                        type="date"
                        value={meterReadingsData[index]?.reading_date || ""}
                        onChange={(e) =>
                          handleMeterReadingChange(
                            meter.id,
                            "reading_date",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-gray-500 text-center py-4">
                No meter data available
              </div>
            )}
          </div>
        </div>
      )}
      {!request?.additionalData?.billId && !billGenerated && (
        <div className="flex justify-end items-center gap-2">
          <button
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded transition disabled:opacity-60"
            onClick={handleGenerateBill}
            disabled={isSubmitting}
          >
            {isSubmitting && (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            )}
            <ReceiptText className="h-5 w-5" />
            Generate Final Bill
          </button>
        </div>
      )}

      {showBillSummary && (billResponse || request?.additionalData?.billId) && (
        <div className="space-y-4">
          {/* Title */}
          <div className="font-bold text-lg text-purple-700">
            {"Final Bill Summary"}
          </div>

          {/* Invoice Info */}
          <div className="bg-white rounded p-4 flex flex-wrap md:flex-nowrap justify-between items-center">
            <div>
              <div className="font-semibold text-base">Invoice Information</div>
              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-40 mt-2 md:mt-5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Invoice No:</span>
                  <span className="text-sm font-semibold">
                    {getBillDisplayData()?.billNumber}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Billing Period:</span>
                  <span className="text-sm">
                    {getBillDisplayData()?.invoicePeriod}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bill Details */}
          <div className="bg-white rounded p-4">
            <div className="font-semibold mb-2">Bill Details</div>
            <div className="flex justify-between">
              <span>Billed Amount</span>
              <span>
                {currency} {getBillDisplayData()?.billAmount || "0.00"}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Outstanding Amount</span>
              <span>
                {currency} {getBillDisplayData()?.totalOutstanding || "0.00"}
              </span>
            </div>
            {applyDeposit && getBillDisplayData()?.securityDeposit > 0 && (
              <div className="flex justify-between text-green-700">
                <span>Security Deposit Applied</span>
                <span>
                  - {currency} {getBillDisplayData()?.securityDeposit || "0.00"}
                </span>
              </div>
            )}
            <hr className="my-2" />
            <div className="flex justify-between font-bold text-lg">
              <span>Final Amount Due</span>
              <span className="text-red-600">
                {currency} {getBillDisplayData()?.additionlaOutstanding}
              </span>
            </div>
          </div>

          {/* Security Deposit Info */}
          <div className="bg-white rounded p-4">
            <div className="font-semibold mb-2 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-gray-600" />
              Security Deposit Information
            </div>
            <div className="flex justify-between">
              <span>Deposit Amount</span>
              <span>
                {currency} {getBillDisplayData()?.securityDeposit}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Applied to Bill</span>
              <span>{applyDeposit ? "Yes" : "No"}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-4">
            {/* Print Bill */}
            <button
              className="flex-1 py-2 rounded bg-blue-600 text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePrintClick(getBillDisplayData().billId)}
              disabled={printBill.isPending} // disable while loading
            >
              {printBill.isPending ? "Printing..." : "Print Bill"}
            </button>

            {billStatus === "pending" && (
              <button
                className="flex-1 py-2 rounded bg-green-600 text-white font-semibold flex items-center justify-center gap-2 hover:bg-green-700 transition"
                onClick={() => setShowApprovalModal(true)}
                disabled={isApprovingBill}
              >
                <Send className="h-4 w-4" />
                Approve
              </button>
            )}

            {/* Only show these buttons if bill is NOT approved */}
            {billStatus !== "approved" && !request?.additionalData?.billId && (
              <>
                <button
                  className="flex-1 py-2 rounded border border-gray-300 bg-gray-50 text-black font-semibold hover:bg-gray-100 transition"
                  onClick={() => handleReject()}
                  disabled={isRejectingBill}
                >
                  Discard and Generate New Bill
                </button>
                <button
                  className="flex-1 py-2 rounded bg-green-600 text-white font-semibold flex items-center justify-center gap-2 hover:bg-green-700 transition"
                  onClick={() => setShowApprovalModal(true)}
                  disabled={isApprovingBill}
                >
                  <Send className="h-4 w-4" />
                  Approve
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {
        /*billStatus === "approved"*/ true && (
          <Card className="overflow-hidden">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <Banknote className="h-5 w-5" /> Refund Settlement
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-5">
              {/* No Refund Empty State */}
              {Number(totalRefundAmount) === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="rounded-full bg-slate-100 p-3 mb-3">
                    <Banknote className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="text-sm font-medium text-slate-600">
                    No Refund Settlement Required
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    There are no outstanding deposits or credits to refund for
                    this account.
                  </p>
                </div>
              ) : (
                <>
                  {/* Info Banner */}
                  <div className="flex items-start gap-2.5 rounded-lg bg-blue-50 border border-blue-100 px-4 py-3">
                    <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-blue-700 leading-relaxed">
                      A refund entry will be automatically created in{" "}
                      <span className="font-semibold">Payments → Refunds</span>{" "}
                      once the consumer account is fully disconnected.
                    </p>
                  </div>

                  {/* Refund Line Items Table */}
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <GenericTable
                      data={refundTableData}
                      columns={refundTableColumns}
                      rowKey="id"
                      getRowClassName={(row: any) =>
                        row.isTotal
                          ? "bg-slate-50 border-t-2 border-slate-200"
                          : ""
                      }
                    />
                  </div>

                  {/* Form fields */}
                  <div>
                    <DynamicForm
                      form={refundForm}
                      fields={refundFields}
                      onSubmit={handleSubmitRefund}
                      onReset={handleCancelRefund}
                      loading={isSubmittingRefund}
                      gridColumns={12}
                      responsiveColumns={{ sm: 12, md: 12 }}
                      config={{
                        layout: "grid",
                        spacing: "medium",
                        showSubmitButton: true,
                        submitButtonText:
                          refundForm.watch("disposition") === "refundToCustomer"
                            ? "Confirm Refund"
                            : "Confirm Credit Note",
                        showResetButton: true,
                        resetButtonText: "Cancel Refund",
                        disableSubmit: !refundForm.watch("confirmTotalRefund"),
                        classes: BUTTON_FIELD_CLASSES,
                      }}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )
      }
      {billStatus === "approved" && (
        <div className="bg-white rounded-lg shadow border p-6">
          <div className="font-bold text-xl mb-4 flex items-center gap-2">
            <XCircle className="h-5 w-5" /> Service Disconnection
          </div>
          <div className="mb-4">
            <div className="font-semibold mb-2">Meter Action Required</div>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="radio"
                checked={meterAction === "unassign"}
                onChange={() => setMeterAction("unassign")}
              />
              <span>Unassign Meter Only</span>
            </label>
            <div className="text-gray-500 text-sm mb-2 ml-6">
              Keep the meter in place but unassign it from this account. Meter
              can be reassigned to future customers.
            </div>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="radio"
                checked={meterAction === "remove"}
                onChange={() => setMeterAction("remove")}
              />
              <span>Remove Meter Completely</span>
            </label>
            <div className="text-gray-500 text-sm mb-2 ml-6">
              Physically remove the meter from the premises. This option is
              typically used when the meter is damaged or the service location
              is being permanently discontinued.
            </div>
            {meterAction === "remove" && (
              <MeterRemovalFormWithService
                ref={formRef}
                utilityServiceList={utilityServiceList}
                loading={submissionLoading}
                consumerData={request}
                // onFormDataChange={(data) => setMeterRemovalFormData(data)}
                // onValidationChange={(isValid) => setIsMeterFormValid(isValid)}
              />
            )}
          </div>
        </div>
      )}

      {/* Account Closure Checklist */}
      {billStatus === "approved" && (
        <div className="bg-white rounded-lg shadow border p-6">
          <div className="font-bold text-xl mb-4">
            Account Closure Checklist
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {checklistItems.map((item, idx) => (
              <label
                key={item}
                className="flex items-center gap-2 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  className="form-checkbox h-5 w-5 text-blue-600"
                  checked={checked[idx]}
                  onChange={() =>
                    setChecked((prev) =>
                      prev.map((v, i) => (i === idx ? !v : v)),
                    )
                  }
                />
                <span className="text-base">{item}</span>
              </label>
            ))}
          </div>

          <div className="text-gray-500 text-sm mb-4">
            Completion: {checked.filter(Boolean).length}/{checklistItems.length}{" "}
            items
          </div>

          <Button
            className="w-full md:w-auto px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded disabled:opacity-60 justify-end"
            onClick={handleCompleteClosure}
          >
            Execute Disconnection
          </Button>
        </div>
      )}

      {/* Service Disconnection */}

      {/* Modal for Bill Generation Flow */}
      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setModalStep(0);
        }}
        title={
          modalStep === 1
            ? "Security Deposit Options"
            : modalStep === 2
              ? "Final Bill Summary"
              : modalStep === 3
                ? "Approve and Send Final Bill"
                : ""
        }
      >
        {renderModalContent()}
      </Modal>
      <Modal
        isOpen={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        title="Approve and Send Final Bill"
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Customer Contact Details */}
          {/* <div className="bg-blue-50 rounded p-4">
            <div className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
              <span>👤</span> Customer Contact Details
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Name:</span>
                <span className="text-sm">{customerContactData.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Email:</span>
                <span className="text-sm">{customerContactData.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Phone:</span>
                <span className="text-sm">{customerContactData.phone}</span>
              </div>
            </div>
          </div> */}
          <div className="grid grid-cols-1 gap-4">
            {/* E-Bill */}
            <Card className="bg-blue-50 border-blue-100">
              <CardContent className="flex items-center justify-between p-4">
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    Send E-Bill
                  </p>
                  <p className="text-xs text-blue-700">
                    Receive your bill via email
                  </p>
                </div>

                <Switch checked={sendEBill} onCheckedChange={setSendEBill} />
              </CardContent>
            </Card>

            {/* Physical Bill */}
            <Card className="bg-green-50 border-green-100">
              <CardContent className="flex items-center justify-between p-4">
                <div>
                  <p className="text-sm font-medium text-green-900">
                    Send Physical Bill
                  </p>
                  <p className="text-xs text-green-700">
                    Receive a printed bill by post
                  </p>
                </div>

                <Switch
                  checked={sendPhysicalBill}
                  onCheckedChange={setSendPhysicalBill}
                />
              </CardContent>
            </Card>
          </div>
          {/* Bill Summary */}

          <div className="bg-green-50 rounded p-4">
            <div className="font-semibold mb-2">Bill Summary</div>

            <div className="flex justify-between">
              <span>Billed Amount</span>
              <span>
                {currency} {billData?.totalAmountPayable ?? 0}
              </span>
            </div>

            <div className="flex justify-between">
              <span>Outstanding Amount</span>
              <span>
                {currency} {billData?.outstandingBalance ?? 0}
              </span>
            </div>

            {applyDeposit && (
              <div className="flex justify-between text-green-700">
                <span>Security Deposit Applied</span>
                <span>
                  -{currency} {billData?.securityDeposit ?? 0}
                </span>
              </div>
            )}

            <div className="flex justify-between font-bold text-lg mt-2">
              <span>Final Amount Due</span>
              <span className="text-red-600">
                {currency}{" "}
                {(() => {
                  const billed = Number(billData?.totalAmountPayable) || 0;
                  const outstanding = Number(billData?.outstandingBalance) || 0;
                  const deposit = Number(billData?.securityDeposit) || 0;

                  const finalAmount =
                    billed + outstanding - (applyDeposit ? deposit : 0);
                  return finalAmount.toFixed(2);
                })()}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-4">
            <button
              className="flex-1 py-2 rounded border border-gray-300 bg-gray-50 text-black font-semibold hover:bg-gray-100 transition"
              onClick={() => setShowApprovalModal(false)}
            >
              Cancel
            </button>
            <button
              className="flex-1 py-2 rounded bg-green-600 text-white font-semibold flex items-center justify-center gap-2 hover:bg-green-700 transition"
              onClick={handleFinalApproval}
              disabled={isApprovingBill}
            >
              {isApprovingBill && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              <Send className="h-4 w-4" />
              Approve
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SettlementClosureTab;
